import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Trash2, Edit2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { DEFAULT_RECEIPT_TYPES, AVAILABLE_COLORS, ReceiptTypeConfig, OCR_MODELS } from '@/config/receipt-types';
import { receiptTypeService } from '@/services/receiptTypeService';
import { useDataMode } from '@/providers/DataModeProvider';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';

export function ReceiptTypesSettings() {
  const { isLiveMode, dataMode } = useDataMode();
  const [receiptTypes, setReceiptTypes] = useState<ReceiptTypeConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newTypeName, setNewTypeName] = useState('');
  const [newTypeColor, setNewTypeColor] = useState('bg-blue-500');
  const [newTypeTextColor, setNewTypeTextColor] = useState('text-white');
  const [newTypeDefault, setNewTypeDefault] = useState(false);
  const [newTypeOcrModel, setNewTypeOcrModel] = useState('prebuilt-receipt');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');
  const [editTextColor, setEditTextColor] = useState('');
  const [editDefault, setEditDefault] = useState(false);
  const [editOcrModel, setEditOcrModel] = useState('prebuilt-receipt');

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [typeToDelete, setTypeToDelete] = useState<{ id: string; name: string } | null>(null);
  useEffect(() => {
    loadReceiptTypes();
  }, [dataMode]);

  const loadReceiptTypes = async () => {
    try {
      setIsLoading(true);
      const types = await receiptTypeService.getAll(isLiveMode);
      setReceiptTypes(types);
    } catch (error) {
      console.error('Failed to load receipt types:', error);
      toast.error('Failed to load receipt types. Using defaults.');
      setReceiptTypes(DEFAULT_RECEIPT_TYPES);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddType = async () => {
    if (!newTypeName.trim()) {
      toast.error('Please enter a receipt type name');
      return;
    }

    // Check for duplicate name
    const duplicateName = receiptTypes.find(
      t => t.name.toLowerCase() === newTypeName.trim().toLowerCase()
    );
    if (duplicateName) {
      toast.error('A receipt type with this name already exists');
      return;
    }

    try {
      // If setting as default, clear other defaults first
      let updatedTypes = receiptTypes;
      if (newTypeDefault) {
        const currentDefault = receiptTypes.find(t => t.default);
        if (currentDefault) {
          await receiptTypeService.update(currentDefault.id, {
            ...currentDefault,
            default: false,
          }, isLiveMode);
          updatedTypes = receiptTypes.map(t => 
            t.id === currentDefault.id ? { ...t, default: false } : t
          );
        }
      }

      const newType = await receiptTypeService.create({
        name: newTypeName.trim(),
        color: newTypeColor,
        textColor: newTypeTextColor,
        default: newTypeDefault,
        ocrModel: newTypeOcrModel,
      }, isLiveMode);

      setReceiptTypes([...updatedTypes, newType]);
      setNewTypeName('');
      setNewTypeColor('bg-blue-500');
      setNewTypeTextColor('text-white');
      setNewTypeDefault(false);
      setNewTypeOcrModel('prebuilt-receipt');
      toast.success('Receipt type added successfully');
    } catch (error) {
      console.error('Failed to add receipt type:', error);
      toast.error('Failed to add receipt type');
    }
  };

  const handleDeleteClick = (type: ReceiptTypeConfig) => {
    setTypeToDelete({ id: type.id, name: type.name });
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteType = async () => {
    if (typeToDelete) {
      await receiptTypeService.delete(typeToDelete.id, isLiveMode);
      setReceiptTypes(receiptTypes.filter((type) => type.id !== typeToDelete.id));
      toast.success('Receipt type deleted');
      setDeleteConfirmOpen(false);
      setTypeToDelete(null);
    }
  };

  const handleStartEdit = (type: ReceiptTypeConfig) => {
    setEditingId(type.id);
    setEditName(type.name);
    setEditColor(type.color);
    setEditTextColor(type.textColor);
    setEditDefault(type.default || false);
    setEditOcrModel(type.ocrModel || 'prebuilt-receipt');
  };

  const handleSaveEdit = async () => {
    if (!editName.trim()) {
      toast.error('Please enter a receipt type name');
      return;
    }

    if (!editingId) return;

    // Check for duplicate name (excluding the current type being edited)
    const duplicateName = receiptTypes.find(
      t => t.id !== editingId && t.name.toLowerCase() === editName.trim().toLowerCase()
    );
    if (duplicateName) {
      toast.error('A receipt type with this name already exists');
      return;
    }

    try {
      // If setting as default, clear other defaults first
      let updatedTypes = receiptTypes;
      if (editDefault) {
        const currentDefault = receiptTypes.find(t => t.default && t.id !== editingId);
        if (currentDefault) {
          await receiptTypeService.update(currentDefault.id, {
            ...currentDefault,
            default: false,
          }, isLiveMode);
          updatedTypes = receiptTypes.map(t => 
            t.id === currentDefault.id ? { ...t, default: false } : t
          );
        }
      }

      await receiptTypeService.update(editingId, {
        name: editName.trim(),
        color: editColor,
        textColor: editTextColor,
        default: editDefault,
        ocrModel: editOcrModel,
      }, isLiveMode);

      setReceiptTypes(
        updatedTypes.map((type) =>
          type.id === editingId
            ? { ...type, name: editName.trim(), color: editColor, textColor: editTextColor, default: editDefault, ocrModel: editOcrModel }
            : type
        )
      );
      setEditingId(null);
      toast.success('Receipt type updated');
    } catch (error) {
      console.error('Failed to update receipt type:', error);
      toast.error('Failed to update receipt type');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditColor('');
    setEditTextColor('');
    setEditDefault(false);
    setEditOcrModel('prebuilt-receipt');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-muted-foreground">Loading receipt types...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
        <h3 className="font-semibold">Add New Receipt Type</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="new-type-name">Name</Label>
            <Input
              id="new-type-name"
              placeholder="e.g., Credit Note"
              value={newTypeName}
              onChange={(e) => setNewTypeName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddType()}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-type-color">Color</Label>
            <div className="flex gap-2 flex-wrap">
              {AVAILABLE_COLORS.map((color) => (
                <button
                  key={color.value}
                  type="button"
                  onClick={() => {
                    setNewTypeColor(color.value);
                    setNewTypeTextColor(color.textColor);
                  }}
                  className={`w-8 h-8 rounded-full ${color.value} ${
                    newTypeColor === color.value
                      ? 'ring-2 ring-offset-2 ring-primary'
                      : 'hover:ring-2 hover:ring-offset-2 hover:ring-gray-300'
                  }`}
                  title={color.name}
                />
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-type-model">Scanning Model</Label>
            <div className="flex items-center gap-4">
              <Select value={newTypeOcrModel} onValueChange={setNewTypeOcrModel}>
                <SelectTrigger id="new-type-model" className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {OCR_MODELS.map((model) => (
                    <SelectItem key={model.id} value={model.id}>
                      {model.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="new-type-default"
                  checked={newTypeDefault}
                  onCheckedChange={(checked) => setNewTypeDefault(checked as boolean)}
                />
                <Label htmlFor="new-type-default" className="cursor-pointer">
                  Set as default
                </Label>
              </div>
            </div>
          </div>
        </div>
        <Button onClick={handleAddType} className="w-full sm:w-auto">
          <Plus className="h-4 w-4 mr-2" />
          Add Receipt Type
        </Button>
      </div>

      <div className="space-y-4">
        <h3 className="font-semibold">Existing Receipt Types</h3>
        <div className="grid gap-3">
          {receiptTypes.map((type) => (
            <div
              key={type.id}
              className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
            >
              {editingId === type.id ? (
                <div className="flex-1 space-y-3">
                  <div className="flex items-center gap-3">
                    <Input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="max-w-xs"
                      onKeyDown={(e) => e.key === 'Enter' && handleSaveEdit()}
                      placeholder="Name"
                    />
                    <div className="flex gap-1">
                      {AVAILABLE_COLORS.map((color) => (
                        <button
                          key={color.value}
                          type="button"
                          onClick={() => {
                            setEditColor(color.value);
                            setEditTextColor(color.textColor);
                          }}
                          className={`w-6 h-6 rounded-full ${color.value} ${
                            editColor === color.value
                              ? 'ring-2 ring-offset-2 ring-primary'
                              : 'hover:ring-2 hover:ring-offset-2 hover:ring-gray-300'
                          }`}
                          title={color.name}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Scanning Model</Label>
                      <div className="flex items-center gap-3">
                        <Select value={editOcrModel} onValueChange={setEditOcrModel}>
                          <SelectTrigger className="w-[200px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {OCR_MODELS.map((model) => (
                              <SelectItem key={model.id} value={model.id}>
                                {model.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id={`edit-default-${type.id}`}
                            checked={editDefault}
                            onCheckedChange={(checked) => setEditDefault(checked as boolean)}
                          />
                          <Label htmlFor={`edit-default-${type.id}`} className="cursor-pointer text-sm">
                            Default
                          </Label>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 ml-auto">
                      <Button size="sm" variant="ghost" onClick={handleSaveEdit}>
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={handleCancelEdit}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 flex-1">
                    <div className={`w-4 h-4 rounded-full ${type.color}`} />
                    <span className="font-medium">{type.name}</span>
                    {type.default && (
                      <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                        Default
                      </span>
                    )}
                    <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-md ml-auto">
                      {OCR_MODELS.find(m => m.id === type.ocrModel)?.name || 'Receipt'}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleStartEdit(type)}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteClick(type)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Receipt Type?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{typeToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteType} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
